import * as L from 'leaflet';

interface MapLocation {
  lat: number;
  lng: number;
  popup?: string;
  post_type: string;
  secteur: string;
}

class PageMap {
  private mapElement: HTMLElement | null;
  private markerLayer: L.LayerGroup | null = null;
  private mapInstance: L.Map | null = null;

  private allLocations: MapLocation[] = [];
  private controlsElement: HTMLElement;
  private applyButton?: HTMLButtonElement;
  private resetButton?: HTMLButtonElement;
  private openButton?: HTMLButtonElement;

  constructor() {
    this.mapElement = document.getElementById('map') as HTMLElement | null;
    this.controlsElement = document.getElementById('controls') as HTMLElement;


    if (!this.mapElement || !this.controlsElement) return;

    // On récupère les boutons
    this.applyButton = this.controlsElement.querySelector('#apply-filters')!;
    this.resetButton = this.controlsElement.querySelector('#reset-filters')!;
    this.openButton = this.controlsElement.querySelector('#open-controls')!;
    this._loadInitialData();
    this._initMap();
    this._initListeners();

  }


  // -----------------------------------------
  // Load Data
  private _loadInitialData(): void {
    const contacts = JSON.parse(this.mapElement?.dataset.contacts || '[]');
    const realisations = JSON.parse(this.mapElement?.dataset.realisations || '[]');
    const evenements = JSON.parse(this.mapElement?.dataset.evenements || '[]');

    this.allLocations = [...contacts, ...realisations, ...evenements];
  }

  // -----------------------------------------
  // Map
  private _initMap(): void {
    this.mapInstance = L.map(this.mapElement).setView([42.15, 9.0], 9);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.mapInstance);

    this.markerLayer = L.layerGroup().addTo(this.mapInstance);
    this._updateMap();
  }

  private _initListeners(): void {
    this.applyButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this._updateMap();
      this.controlsElement.classList.remove('active');
    });

    this.resetButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this._resetFilters();
    });

    this.openButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this._openControls();
    });
  }

  private _resetFilters(): void {
    this.controlsElement.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => cb.checked = true);
    this.controlsElement.querySelector<HTMLSelectElement>('#region-filter')!.value = '';
    this._updateMap();
  }

  private _openControls(): void {
    this.controlsElement.classList.toggle('active');
  }

  private _updateMap(): void {
    if (!this.markerLayer || !this.mapInstance) return;

    // 1. Récupérer l'état actuel des filtres
    const selectedTypes = Array.from(this.controlsElement.querySelectorAll<HTMLInputElement>('input[name="post_type"]:checked')).map(cb => cb.value);
    const selectedSecteur = this.controlsElement.querySelector<HTMLSelectElement>('#secteur-filter')!.value;

    // 2. Filtrer la liste principale de localisations
    const filteredLocations = this.allLocations.filter(loc => {
      const typeMatch = selectedTypes.includes(loc.post_type);
      const regionMatch = selectedSecteur === '' || loc.secteur === selectedSecteur;
      return typeMatch && regionMatch;
    });

    // 3. Mettre à jour la carte
    this.markerLayer.clearLayers();
    const markerBounds: L.LatLngExpression[] = [];

    // On crée un dictionnaire d'icônes pour ne pas les recréer à chaque fois
    const icons = {
      contact: L.icon({ iconUrl: this.mapElement?.dataset.contactIcon!, iconSize: [31, 42], iconAnchor: [16, 42], popupAnchor: [0, -42] }),
      realisation: L.icon({ iconUrl: this.mapElement?.dataset.realisationIcon!, iconSize: [31, 42], iconAnchor: [16, 42], popupAnchor: [0, -42] }),
      evenement: L.icon({ iconUrl: this.mapElement?.dataset.evenementIcon!, iconSize: [31, 42], iconAnchor: [16, 42], popupAnchor: [0, -42] }),
    };

    filteredLocations.forEach(loc => {
      const marker = L.marker([loc.lat, loc.lng], { icon: icons[loc.post_type] })
        .addTo(this.markerLayer!);

      if (loc.popup) marker.bindPopup(loc.popup);
      markerBounds.push([loc.lat, loc.lng]);
    });

    // 4. Ajuster le zoom
    if (markerBounds.length > 0 && this.mapElement) {
      const leftPadding = this.mapElement.offsetWidth * 0.30;

      this.mapInstance.fitBounds(markerBounds, {
        paddingTopLeft: [leftPadding, 50],
        paddingBottomRight: [50, 50],
        maxZoom: 16
      });
    }
  }
}

export const pageMap = new PageMap();
